
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from './useNotifications';

interface LocationDetails {
  name: string;
  city: string;
  country: string;
  continent: string;
  latitude: number;
  longitude: number;
}

interface PodcastData {
  Name: string;
  Country: string;
  City: string;
  Continent: string;
  Description_FR: string;
  Description_EN: string;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Latitude: number;
  Longitude: number;
  Image: string;
}

interface ExistingPodcast {
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Latitude: number;
  Longitude: number;
}

export const usePodcastGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPodcast, setGeneratedPodcast] = useState<PodcastData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<('fr' | 'en')[]>(['fr', 'en']);
  const [isLocationBlocked, setIsLocationBlocked] = useState(false);
  
  const { sendPodcastCreatedNotification } = useNotifications();

  const checkExistingPodcasts = async (locationDetails: LocationDetails) => {
    try {
      console.log('🔍 Checking for existing podcasts within a 50-meter radius of:', {
        name: locationDetails.name,
        coordinates: [locationDetails.latitude, locationDetails.longitude]
      });

      // Fetch all podcasts
      const { data: allPodcasts, error } = await supabase
        .from('podcasts')
        .select('AudioFile_FR, AudioFile_EN, Latitude, Longitude')
        .not('Latitude', 'is', null)
        .not('Longitude', 'is', null);

      if (error) {
        console.error('Error fetching podcasts:', error);
        return;
      }
      
      // Calculate distance between two points on Earth using Haversine formula
      const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const toRad = (value: number) => (value * Math.PI) / 180;
        const R = 6371e3; // metres
        const φ1 = toRad(lat1);
        const φ2 = toRad(lat2);
        const Δφ = toRad(lat2 - lat1);
        const Δλ = toRad(lon2 - lon1);

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                  Math.cos(φ1) * Math.cos(φ2) *
                  Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // in metres
      };

      // Filter podcasts within a 50-meter radius
      const existingPodcasts = allPodcasts?.filter((podcast: ExistingPodcast) => {
        const distance = getDistance(
          locationDetails.latitude,
          locationDetails.longitude,
          podcast.Latitude,
          podcast.Longitude
        );
        // We consider a podcast "existing" if it is within 50 meters
        return distance <= 50; 
      }) || [];

      console.log('📍 Found existing podcasts nearby:', existingPodcasts);

      // Determine which languages are already used from the nearby podcasts
      const usedLanguages: ('fr' | 'en')[] = [];
      
      existingPodcasts.forEach((podcast: ExistingPodcast) => {
        if (podcast.AudioFile_FR) {
          usedLanguages.push('fr');
        }
        if (podcast.AudioFile_EN) {
          usedLanguages.push('en');
        }
      });

      // Remove duplicates
      const uniqueUsedLanguages = [...new Set(usedLanguages)];
      
      console.log('🗣️ Languages already used:', uniqueUsedLanguages);

      // Calculate available languages
      const available = (['fr', 'en'] as const).filter(lang => !uniqueUsedLanguages.includes(lang));
      
      setAvailableLanguages(available);
      setIsLocationBlocked(available.length === 0);

      console.log('✅ Available languages:', available);
      console.log('🚫 Location blocked:', available.length === 0);

    } catch (error) {
      console.error('Error checking existing podcasts:', error);
      // In case of error, allow all languages
      setAvailableLanguages(['fr', 'en']);
      setIsLocationBlocked(false);
    }
  };

  const generatePodcast = async (locationDetails: LocationDetails, selectedLanguage: 'fr' | 'en') => {
    setIsGenerating(true);
    setError(null);
    setGeneratedPodcast(null);

    try {
      // Get current user session to include user_id
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      // Ensure user is authenticated and has a valid user_id
      if (!userId) {
        throw new Error('Utilisateur non authentifié');
      }

      console.log('📤 Sending podcast creation request with user_id:', userId);

      const { data: podcastData, error: functionError } = await supabase.functions.invoke('generate-podcast-proxy-with-rate-limit', {
        body: {
          lieu: locationDetails.name,
          pays: locationDetails.country,
          langue_souhaitee: selectedLanguage,
          city: locationDetails.city,
          latitude: locationDetails.latitude,
          longitude: locationDetails.longitude,
          continent: locationDetails.continent,
          user_id: userId // Include user_id in the request
        }
      });

      if (functionError) {
        throw new Error(functionError.message || 'Erreur lors de l\'appel à la fonction proxy');
      }

      if (!podcastData) {
        throw new Error('Aucune donnée reçue du proxy');
      }
      setGeneratedPodcast(podcastData);
      toast.success('Podcast généré avec succès !');
      
      // Send notification
      await sendPodcastCreatedNotification(locationDetails.name);
      
      // Call super-service to enrich information in background (don't await)
      supabase.functions.invoke('super-service', {
        body: {
          Name: podcastData.Name,
          City: podcastData.City,
          Country: podcastData.Country,
          Latitude: podcastData.Latitude,
          Longitude: podcastData.Longitude
        }
      }).then(() => {
        console.log('Super-service called successfully for podcast enrichment');
      }).catch((enrichError) => {
        console.error('Error calling super-service:', enrichError);
      });
      
      return podcastData;

    } catch (error: any) {
      console.error("Error calling Cloud Function:", error);
      const errorMessage = error.message || 'Erreur lors de la génération du podcast';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const reset = () => {
    setIsGenerating(false);
    setGeneratedPodcast(null);
    setError(null);
    setAvailableLanguages(['fr', 'en']);
    setIsLocationBlocked(false);
  };

  return {
    isGenerating,
    generatedPodcast,
    error,
    availableLanguages,
    isLocationBlocked,
    generatePodcast,
    checkExistingPodcasts,
    reset
  };
};
